import React, { useRef, useEffect } from 'react';

import { Button, Input, SelectButtons } from '../../';
import { CustomFilterControl } from './CustomFilterControl';
import { keyCode, formatData, KEYBOARD_SHORTCUT } from '../../../helpers';
import { useTooltip } from '../../../hooks';

/**
 * The bar above a DataTable: its title, the buttons that act on the selection, the view's own
 * controls, the filter box and the view mode picker.
 *
 * @param {object} props
 * @param {string} [props.title]                          - Table title, shown first.
 * @param {object[]} [props.buttons]                      - Toolbar buttons. A main button always
 *                                                          shows; the rest act on the selection.
 * @param {boolean} [props.editable=true]                 - Hides the buttons entirely when false.
 * @param {number} [props.itemTotalCount=0]               - Rows the table holds, filtered or not.
 * @param {object[]} [props.selectedItems]                - The rows currently selected.
 * @param {number} [props.selectedItemsCount]             - How many, for the "n selected" text.
 * @param {number} [props.itemCount]                      - Rows a filter leaves, for "showing x of y".
 * @param {boolean} [props.filterable]                    - Whether the filter box is offered.
 * @param {function} props.requestFilter                  - Applies a filter term to the table.
 * @param {string} [props.filterTerm]                     - The term currently filtered on.
 * @param {object[]} [props.filterControls]               - Controls the view adds to the toolbar,
 *                                                          each a CustomFilterControl definition.
 * @param {'left'|'right'} [props.filterControlsPosition='left'] - Which side of the filter box those
 *                                                          controls sit on.
 * @param {function} [props.onFilterObjectsFilter]        - Called when the filter's column selection
 *                                                          changes.
 * @param {object[]} [props.filterObjects]                - The columns the filter can match on.
 * @param {object[]} [props.viewModes]                    - View mode options, when the table offers them.
 * @param {string} [props.viewMode]                       - The view mode in effect.
 * @param {function} [props.onViewModeChange]             - Called with the view mode chosen.
 */
export const DataTableToolbar = ({
  title,
  buttons,
  editable = true,
  itemTotalCount = 0,
  selectedItems,
  selectedItemsCount,
  itemCount,
  filterable,
  requestFilter,
  filterTerm,
  filterControls,
  filterControlsPosition = 'left',
  onFilterObjectsFilter,
  filterObjects,
  viewModes,
  viewMode,
  onViewModeChange,
}) => {
  const inputRef = useRef();
  const { showTooltip, hideTooltip } = useTooltip();

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);

    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleKeyDown = e => {
    if ((e.ctrlKey || e.metaKey) && e.keyCode === keyCode.F) {
      e.preventDefault();
      inputRef.current.focus();
    }

    if (e.keyCode === keyCode.ESCAPE) {
      requestFilter('');
      setTimeout(() => inputRef.current.blur(), 100);
    }
  };
  const handleInputChange = event => {
    let { value } = '';

    // Handle standard form inputs
    if (event.target !== undefined) {
      value = event.target.value;

      // Handle custom form inputs
    } else {
      value = event.value;
    }

    if (value === ' ' && filterTerm === '') {
      // do nothing
    } else {
      requestFilter(value);
    }
  };

  /**
   * Returns the first disabled condition that is currently triggered, or null if none.
   * button.disabled may be a single condition object or an array of condition objects.
   * Each condition: { field, value, label?, tooltip? }
   * A condition is triggered when ALL selected items satisfy field === value.
   */
  const getTriggeredDisabledCondition = button => {
    if (!button.disabled) return null;
    const conditions = Array.isArray(button.disabled) ? button.disabled : [button.disabled];
    return (
      conditions.find(
        cond =>
          selectedItems.length > 0 && selectedItems.every(item => item[cond.field] === cond.value)
      ) ?? null
    );
  };

  /** Returns true if any disabled condition is currently triggered. */
  const isDisabled = button => getTriggeredDisabledCondition(button) !== null;

  /**
   * Returns true if the button should be hidden. button.hidden may be a single condition
   * object or an array. A condition is triggered (button hidden) when ALL selected items
   * satisfy field === value.
   */
  const isHidden = button => {
    if (!button.hidden) return false;
    const conditions = Array.isArray(button.hidden) ? button.hidden : [button.hidden];
    return conditions.some(cond => selectedItems.every(item => item[cond.field] === cond.value));
  };

  // The view's own controls, rendered on whichever side of the filter box the view asked for. Built
  // once and placed below rather than written out twice.
  const filterControlsBlock = filterControls?.length > 0 && (
    <div className="main-toolbar-item filter-controls-container">
      {filterControls.map((filterControl, index) => {
        return <CustomFilterControl {...filterControl} key={index} requestFilter={requestFilter} />;
      })}
    </div>
  );

  return (
    <div className="DataTableToolbar main-toolbar">
      {title && <div className="main-toolbar-item main-toolbar-title">{title}</div>}

      {editable && (
        <React.Fragment>
          {buttons &&
            buttons
              .filter(x => Object.keys(x).length > 0)
              .map((button, index) => {
                if (button.customButton) {
                  return <React.Fragment key={index}>{button.customButton}</React.Fragment>;
                } else if (button.mainButton) {
                  return (
                    <Button
                      key={index}
                      buttonStyle="main-button"
                      value={button.label}
                      className="main-toolbar-item"
                      onClick={() => button.onClick()}
                      title={button.tooltip}
                    />
                    // <span>{button.label}</span>
                    //</Button>
                  );
                } else {
                  return null;
                }
              })}

          {selectedItemsCount > 0 && (
            <div className="selected-items-options main-toolbar-item">
              {buttons &&
                buttons
                  .filter(x => Object.keys(x).length > 0)
                  .map((button, index) => {
                    if (!(button.mainButton || button.customButton)) {
                      if (isHidden(button)) return null;
                      const triggeredCondition = getTriggeredDisabledCondition(button);
                      const disabled = triggeredCondition !== null;
                      return (
                        <Button
                          key={index}
                          value={
                            disabled && triggeredCondition.label
                              ? triggeredCondition.label
                              : button.label
                          }
                          className="main-toolbar-item"
                          onClick={() => button.onClick()}
                          tooltip={(disabled && triggeredCondition.tooltip) || button.tooltip}
                          disabled={disabled}
                        />
                      );
                    } else {
                      return null;
                    }
                  })}

              <div className="main-toolbar-item main-toolbar-text">
                {selectedItemsCount === itemTotalCount ? 'All ' : ''}
                {formatData(selectedItemsCount, 'integer')}{' '}
                {selectedItemsCount > 1 || selectedItemsCount === itemTotalCount
                  ? 'assets'
                  : 'asset'}{' '}
                selected
              </div>
            </div>
          )}
        </React.Fragment>
      )}
      {filterControlsPosition !== 'right' && filterControlsBlock}
      {filterable && (
        <div
          className="filter-container"
          onMouseEnter={() => showTooltip({ title: `Filter table (${KEYBOARD_SHORTCUT.filter})` })}
          onMouseLeave={hideTooltip}
        >
          {filterTerm && (
            <div className="main-toolbar-text">{`Showing ${itemCount} of ${itemTotalCount} assets`}</div>
          )}
          <Input
            ref={inputRef}
            type="text"
            name="table_filter"
            value={filterTerm}
            onChange={handleInputChange}
            placeholder="Filter Table..."
            isNullable={true}
            icon="filter"
            iconAsButton={true}
            withSelect={true}
            selectProps={[
              {
                name: 'filter_object',
                value: filterObjects.filter(x => x.checked),
                tooltip: 'Select columns to filter on',
                options: filterObjects,
                onChange: onFilterObjectsFilter,
              },
            ]}
          />
        </div>
      )}
      {filterControlsPosition === 'right' && filterControlsBlock}
      {viewModes && (
        <div className="main-toolbar-item view-modes-container">
          <SelectButtons
            name="view_mode"
            value={viewMode}
            options={viewModes}
            onChange={onViewModeChange}
            title="Select view mode"
            design="bright"
          />
        </div>
      )}
    </div>
  );
};
